import { useState } from 'react'
import { renderWithMentions } from './MentionInput'

/**
 * The boundary an activity-run completion draws between its OUTCOME and its evidence
 * (ADR-160 D1). Must match DisclosureMarker in
 * mission-control/backend/internal/chat/activity_outcome.go.
 */
export const DISCLOSURE_MARKER = '─── full step output ───'

/**
 * Renders a chat message, collapsing an activity run's raw step output.
 *
 * A successful 15-step run measured 53,638 characters across 336 raw JSON blobs, with
 * `RESULT APPEND_OK …` at roughly character 30,000. The evidence is worth keeping — it is
 * how the false "(upload failed)" was eventually diagnosed — but pasting it above the
 * answer is where the answer goes to hide.
 *
 * A message with no marker renders exactly as before, so every other message in the
 * fleet is untouched and an older MC (which emits no marker) degrades to today's
 * behaviour rather than to a broken one.
 */
export function RunOutput({ content }: { content: string }) {
  const [open, setOpen] = useState(false)
  const at = content.indexOf(DISCLOSURE_MARKER)
  if (at < 0) return <>{renderWithMentions(content)}</>

  const outcome = content.slice(0, at).trimEnd()
  const evidence = content.slice(at + DISCLOSURE_MARKER.length).trim()
  const lines = evidence ? evidence.split('\n').length : 0

  return (
    <>
      {renderWithMentions(outcome)}
      {evidence && (
        <div className="mt-2">
          <button
            type="button"
            onClick={() => setOpen(o => !o)}
            className="text-xs text-indigo-700 hover:text-indigo-900 underline"
            aria-expanded={open}
          >
            {open
              ? 'Hide full step output'
              : `Show full step output (${lines} lines, ${evidence.length.toLocaleString()} characters)`}
          </button>
          {open && (
            <pre className="mt-1 max-h-96 overflow-auto whitespace-pre-wrap break-words rounded bg-gray-50 border border-gray-200 p-2 text-[11px] text-gray-700">
              {evidence}
            </pre>
          )}
        </div>
      )}
    </>
  )
}
